import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { templatesApi } from '../../api/client';
import { useAuthStore } from '../../store/authStore';
import { ProjectTemplate, TemplateVersion, Department } from '../../types';
import {
  Plus, X, GripVertical, FileText, Save, Trash2, Info,
  ChevronRight, Edit2, Copy, Archive, ArchiveRestore, Clock, AlertTriangle,
} from 'lucide-react';

// ─── Design tokens ───────────────────────────────────────────────────────────

const C = {
  bg: '#0a0a0f',
  card: '#12121a',
  border: '#1e1e2e',
  borderHover: '#2e2e42',
  textPrimary: '#e2e1f0',
  textSecondary: '#6b6880',
  textMuted: '#4a4858',
  orange: '#f97316',
  orangeHover: '#ea580c',
  success: '#22c55e',
  error: '#ef4444',
  warning: '#f59e0b',
};

// ─── Dept config ──────────────────────────────────────────────────────────────

const ALL_DEPTS: Department[] = ['gestor', 'financiero', 'imprenta', 'dtf', 'almacen', 'embalaje'];

const DEPT_COLORS: Record<Department, string> = {
  gestor: '#3b82f6',
  financiero: '#8b5cf6',
  imprenta: '#ec4899',
  dtf: '#f97316',
  almacen: '#22c55e',
  embalaje: '#14b8a6',
};

const DEPT_LABELS: Record<Department, string> = {
  gestor: 'Gestor',
  financiero: 'Financiero',
  imprenta: 'Imprenta',
  dtf: 'DTF',
  almacen: 'Almacén',
  embalaje: 'Embalaje',
};

// ─── Shared input styles ──────────────────────────────────────────────────────

const inputStyle: React.CSSProperties = {
  width: '100%', background: '#0a0a0f', border: `1px solid ${C.border}`,
  borderRadius: 8, color: C.textPrimary, fontSize: 13, padding: '10px 12px',
  fontFamily: 'DM Sans, sans-serif', outline: 'none', boxSizing: 'border-box',
};

const labelStyle: React.CSSProperties = {
  fontSize: 11, fontWeight: 600, color: C.textMuted, letterSpacing: '0.06em',
  fontFamily: 'JetBrains Mono, monospace', display: 'block', marginBottom: 6,
};

// ─── Mini step flow visualization ─────────────────────────────────────────────

function StepFlowPreview({ steps }: { steps: ProjectTemplate['steps'] }) {
  const sorted = [...steps].sort((a, b) => a.order - b.order);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
      {sorted.map((step, idx) => (
        <React.Fragment key={`${step.department}-${idx}`}>
          {idx > 0 && <ChevronRight size={10} color={C.textMuted} />}
          <span style={{
            padding: '3px 8px', borderRadius: 5,
            background: `${DEPT_COLORS[step.department]}18`,
            border: `1px solid ${DEPT_COLORS[step.department]}40`,
            color: DEPT_COLORS[step.department],
            fontSize: 10, fontWeight: 600, fontFamily: 'DM Sans, sans-serif',
          }}>
            {DEPT_LABELS[step.department]}
          </span>
        </React.Fragment>
      ))}
    </div>
  );
}

// ─── Flow builder (drag & drop) ───────────────────────────────────────────────

function FlowBuilder({ flow, setFlow }: { flow: Department[]; setFlow: (f: Department[]) => void }) {
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [overIdx, setOverIdx] = useState<number | null>(null);

  const available = ALL_DEPTS.filter(d => !flow.includes(d));

  const handleDrop = (e: React.DragEvent, targetIdx: number) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === targetIdx) { setDragIdx(null); setOverIdx(null); return; }
    const next = [...flow];
    const [item] = next.splice(dragIdx, 1);
    next.splice(targetIdx, 0, item);
    setFlow(next);
    setDragIdx(null);
    setOverIdx(null);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Available */}
      <div>
        <label style={labelStyle}>DEPARTAMENTOS DISPONIBLES</label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, padding: 14, background: C.bg, border: `1px solid ${C.border}`, borderRadius: 10, minHeight: 50 }}>
          {available.length === 0 ? (
            <span style={{ fontSize: 12, color: C.textMuted, fontStyle: 'italic' }}>Todos en el flujo</span>
          ) : available.map(d => (
            <button key={d} onClick={() => setFlow([...flow, d])} style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              padding: '5px 10px', borderRadius: 7,
              background: `${DEPT_COLORS[d]}12`, border: `1px solid ${DEPT_COLORS[d]}40`,
              color: DEPT_COLORS[d], fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif',
            }}>
              <Plus size={11} /> {DEPT_LABELS[d]}
            </button>
          ))}
        </div>
      </div>

      {/* Flow */}
      <div>
        <label style={labelStyle}>FLUJO (arrastra para reordenar)</label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {flow.length === 0 ? (
            <div style={{ padding: 20, background: C.bg, border: `2px dashed ${C.border}`, borderRadius: 10, textAlign: 'center', color: C.textMuted, fontSize: 12 }}>
              Añade departamentos
            </div>
          ) : flow.map((dept, idx) => (
            <div
              key={`${dept}-${idx}`}
              draggable
              onDragStart={e => { e.dataTransfer.effectAllowed = 'move'; setDragIdx(idx); }}
              onDragOver={e => { e.preventDefault(); setOverIdx(idx); }}
              onDrop={e => handleDrop(e, idx)}
              onDragEnd={() => { setDragIdx(null); setOverIdx(null); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
                background: overIdx === idx ? `${DEPT_COLORS[dept]}10` : C.card,
                border: overIdx === idx ? `1px solid ${DEPT_COLORS[dept]}50` : `1px solid ${C.border}`,
                borderRadius: 9, cursor: 'grab', opacity: dragIdx === idx ? 0.4 : 1, transition: 'all 0.1s',
              }}
            >
              <div style={{ width: 20, height: 20, borderRadius: 5, background: `${DEPT_COLORS[dept]}22`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: DEPT_COLORS[dept], fontFamily: 'JetBrains Mono, monospace', flexShrink: 0 }}>
                {idx + 1}
              </div>
              <GripVertical size={13} color={C.textMuted} />
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: DEPT_COLORS[dept], display: 'inline-block', flexShrink: 0 }} />
              <span style={{ flex: 1, fontSize: 12, fontWeight: 600, color: C.textPrimary }}>{DEPT_LABELS[dept]}</span>
              <button onClick={() => setFlow(flow.filter((_, i) => i !== idx))} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted, display: 'flex', padding: 2 }}>
                <X size={13} />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Preview */}
      {flow.length > 0 && (
        <div>
          <label style={labelStyle}>VISTA PREVIA</label>
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 4, padding: '12px 16px', background: C.bg, border: `1px solid ${C.border}`, borderRadius: 9 }}>
            {flow.map((dept, idx) => (
              <React.Fragment key={`prev-${dept}-${idx}`}>
                {idx > 0 && <ChevronRight size={12} color={C.textMuted} />}
                <span style={{ padding: '3px 10px', borderRadius: 6, background: `${DEPT_COLORS[dept]}18`, border: `1px solid ${DEPT_COLORS[dept]}40`, color: DEPT_COLORS[dept], fontSize: 11, fontWeight: 600 }}>
                  {DEPT_LABELS[dept]}
                </span>
              </React.Fragment>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Versions modal ───────────────────────────────────────────────────────────

function VersionsModal({
  templateId,
  templateName,
  onClose,
}: {
  templateId: number;
  templateName: string;
  onClose: () => void;
}) {
  const { data, isLoading } = useQuery({
    queryKey: ['template-versions', templateId],
    queryFn: () => templatesApi.getVersions(templateId),
  });

  const versions: TemplateVersion[] = data?.data ?? [];

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 300, backdropFilter: 'blur(4px)' }} />
      <div style={{
        position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        zIndex: 301, width: '90%', maxWidth: 560, maxHeight: '80vh', overflowY: 'auto',
        background: C.card, border: `1px solid ${C.border}`, borderRadius: 16,
        boxShadow: '0 24px 64px rgba(0,0,0,0.6)',
      }}>
        {/* Header */}
        <div style={{ padding: '20px 24px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(249,115,22,0.12)', border: '1px solid rgba(249,115,22,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Clock size={16} color={C.orange} />
          </div>
          <div style={{ flex: 1 }}>
            <h2 style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 16, color: '#fff', margin: 0 }}>
              Historial de versiones
            </h2>
            <p style={{ fontSize: 11, color: C.textMuted, margin: 0 }}>{templateName}</p>
          </div>
          <button onClick={onClose} style={{ width: 32, height: 32, borderRadius: 8, border: `1px solid ${C.border}`, background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.textSecondary }}>
            <X size={15} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          {isLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
              <div style={{ width: 28, height: 28, border: `3px solid ${C.border}`, borderTopColor: C.orange, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
            </div>
          ) : versions.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 32, color: C.textMuted, fontSize: 13 }}>
              Sin historial de versiones
            </div>
          ) : versions.map((v, idx) => (
            <div key={`v${v.version}-${idx}`} style={{
              padding: '14px 16px', borderRadius: 10,
              background: v.isCurrent ? 'rgba(249,115,22,0.06)' : C.bg,
              border: `1px solid ${v.isCurrent ? 'rgba(249,115,22,0.3)' : C.border}`,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, fontSize: 13, color: v.isCurrent ? C.orange : C.textPrimary }}>
                    v{v.version}
                  </span>
                  {v.isCurrent && (
                    <span style={{ padding: '2px 8px', borderRadius: 20, background: 'rgba(249,115,22,0.15)', border: '1px solid rgba(249,115,22,0.4)', color: C.orange, fontSize: 10, fontWeight: 600 }}>
                      ACTUAL
                    </span>
                  )}
                </div>
                {v.changedAt && (
                  <span style={{ fontSize: 11, color: C.textMuted, fontFamily: 'JetBrains Mono, monospace' }}>
                    {new Date(v.changedAt).toLocaleString('es-ES', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </span>
                )}
              </div>
              {v.changedBy && (
                <p style={{ fontSize: 11, color: C.textSecondary, margin: '0 0 8px' }}>Modificado por: {v.changedBy}</p>
              )}
              <StepFlowPreview steps={v.steps} />
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

// ─── Template modal ───────────────────────────────────────────────────────────

function TemplateModal({
  template,
  onClose,
  onSaved,
  onDeleted,
  isMobile,
}: {
  template?: ProjectTemplate;
  onClose: () => void;
  onSaved: (t: ProjectTemplate) => void;
  onDeleted?: (id: number) => void;
  isMobile: boolean;
}) {
  const qc = useQueryClient();
  const isNew = !template;

  const initFlow = template
    ? [...template.steps].sort((a, b) => a.order - b.order).map(s => s.department)
    : ['gestor'] as Department[];

  const [name, setName] = useState(template?.name || '');
  const [description, setDescription] = useState(template?.description || '');
  const [flow, setFlow] = useState<Department[]>(initFlow);
  const [isDefault, setIsDefault] = useState(template?.isDefault ?? false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Count active projects for existing templates
  const { data: activeCountData } = useQuery({
    queryKey: ['template-active-count', template?.id],
    queryFn: () => templatesApi.getActiveProjectsCount(template!.id),
    enabled: !isNew,
    retry: false,
  });
  const activeProjectsCount: number = activeCountData?.count ?? 0;

  // Detect if steps changed
  const originalFlow = template
    ? [...template.steps].sort((a, b) => a.order - b.order).map(s => s.department)
    : [];
  const stepsChanged = !isNew && JSON.stringify(flow) !== JSON.stringify(originalFlow);
  const showVersionWarning = !isNew && stepsChanged && activeProjectsCount > 0;

  const handleSave = async () => {
    if (!name.trim()) { setError('El nombre es obligatorio'); return; }
    if (flow.length === 0) { setError('El flujo debe tener al menos un departamento'); return; }
    setSaving(true);
    setError(null);
    const payload = {
      name: name.trim(),
      description: description.trim(),
      isDefault,
      steps: flow.map((dept, idx) => ({ department: dept, order: idx, required: true })),
    };
    try {
      let result: ProjectTemplate;
      if (isNew) {
        result = await templatesApi.create(payload);
      } else {
        result = await templatesApi.update(template!.id, payload);
      }
      qc.invalidateQueries({ queryKey: ['templates'] });
      onSaved(result);
    } catch {
      const saved: ProjectTemplate = {
        id: template?.id || Date.now(),
        name: payload.name,
        description: payload.description,
        steps: payload.steps,
        version: template?.version ?? 1,
        isArchived: template?.isArchived ?? false,
        isDefault: template?.isDefault ?? false,
        createdAt: template?.createdAt || new Date().toISOString(),
      };
      onSaved(saved);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!template) return;
    setDeleting(true);
    try {
      await templatesApi.delete(template.id);
      qc.invalidateQueries({ queryKey: ['templates'] });
      onDeleted?.(template.id);
    } catch {
      onDeleted?.(template.id);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 200, backdropFilter: 'blur(4px)' }} />
      <div style={{
        position: 'fixed',
        top: isMobile ? 0 : '50%',
        left: isMobile ? 0 : '50%',
        transform: isMobile ? 'none' : 'translate(-50%, -50%)',
        zIndex: 201,
        width: isMobile ? '100%' : '90%',
        maxWidth: isMobile ? 'none' : 600,
        height: isMobile ? '100%' : 'auto',
        maxHeight: isMobile ? '100%' : '90vh',
        overflowY: 'auto',
        background: C.card,
        border: `1px solid ${C.border}`,
        borderRadius: isMobile ? 0 : 16,
        boxShadow: '0 24px 64px rgba(0,0,0,0.6)',
      }}>
        {/* Header */}
        <div style={{ padding: '20px 24px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(249,115,22,0.12)', border: '1px solid rgba(249,115,22,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <FileText size={16} color={C.orange} />
          </div>
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 10 }}>
            <h2 style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 18, color: '#fff', margin: 0, letterSpacing: '-0.02em' }}>
              {isNew ? 'Nueva Plantilla' : 'Editar Plantilla'}
            </h2>
            {!isNew && template?.version && (
              <span style={{
                padding: '2px 8px', borderRadius: 20,
                background: 'rgba(249,115,22,0.15)', border: '1px solid rgba(249,115,22,0.4)',
                color: C.orange, fontSize: 11, fontWeight: 700, fontFamily: 'JetBrains Mono, monospace',
              }}>
                v{template.version}
              </span>
            )}
          </div>
          <button onClick={onClose} style={{ width: 32, height: 32, borderRadius: 8, border: `1px solid ${C.border}`, background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.textSecondary }}>
            <X size={15} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: 18 }}>
          {/* Version bump warning */}
          {showVersionWarning && (
            <div style={{
              display: 'flex', alignItems: 'flex-start', gap: 10, padding: '12px 14px',
              background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 9,
            }}>
              <AlertTriangle size={14} color={C.warning} style={{ flexShrink: 0, marginTop: 1 }} />
              <span style={{ fontSize: 12, color: C.warning, lineHeight: 1.5 }}>
                Esta plantilla tiene <strong>{activeProjectsCount}</strong> proyecto{activeProjectsCount !== 1 ? 's' : ''} activo{activeProjectsCount !== 1 ? 's' : ''}.
                Al guardar se incrementará la versión automáticamente (v{template!.version} → v{template!.version + 1}).
              </span>
            </div>
          )}

          <div>
            <label style={labelStyle}>NOMBRE *</label>
            <input style={inputStyle} placeholder="DTF Estándar" value={name} onChange={e => setName(e.target.value)} />
          </div>
          <div>
            <label style={labelStyle}>DESCRIPCIÓN</label>
            <textarea style={{ ...inputStyle, minHeight: 64, resize: 'vertical' }} placeholder="Descripción de la plantilla..." value={description} onChange={e => setDescription(e.target.value)} />
          </div>

          <FlowBuilder flow={flow} setFlow={setFlow} />

          {/* isDefault toggle */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', background: C.bg, border: `1px solid ${C.border}`, borderRadius: 9 }}>
            <div>
              <div style={{ fontSize: 13, color: C.textPrimary, fontWeight: 500 }}>Plantilla por defecto</div>
              <div style={{ fontSize: 11, color: C.textMuted }}>Se pre-seleccionará al crear nuevos proyectos</div>
            </div>
            <button onClick={() => setIsDefault(v => !v)} style={{ width: 44, height: 24, borderRadius: 12, border: 'none', cursor: 'pointer', background: isDefault ? C.orange : '#2a2a3a', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}>
              <div style={{ position: 'absolute', top: 3, left: isDefault ? 22 : 3, width: 18, height: 18, borderRadius: '50%', background: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 4px rgba(0,0,0,0.3)' }} />
            </button>
          </div>
        </div>

        {error && (
          <div style={{ margin: '0 24px 12px', padding: '10px 14px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, fontSize: 12, color: C.error }}>
            {error}
          </div>
        )}

        {/* Footer */}
        <div style={{ padding: '16px 24px', borderTop: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            {!isNew && !confirmDelete && (
              <button onClick={() => setConfirmDelete(true)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: `1px solid rgba(239,68,68,0.3)`, background: 'rgba(239,68,68,0.08)', color: C.error, fontSize: 12, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' }}>
                <Trash2 size={13} /> Eliminar
              </button>
            )}
            {confirmDelete && (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <span style={{ fontSize: 12, color: C.error }}>¿Confirmar?</span>
                <button onClick={handleDelete} disabled={deleting} style={{ padding: '7px 12px', borderRadius: 7, border: 'none', background: C.error, color: '#fff', fontSize: 11, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' }}>
                  {deleting ? '...' : 'Sí, eliminar'}
                </button>
                <button onClick={() => setConfirmDelete(false)} style={{ padding: '7px 12px', borderRadius: 7, border: `1px solid ${C.border}`, background: 'transparent', color: C.textSecondary, fontSize: 11, cursor: 'pointer' }}>
                  Cancelar
                </button>
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={onClose} style={{ padding: '9px 18px', borderRadius: 9, border: `1px solid ${C.border}`, background: 'transparent', color: C.textSecondary, fontSize: 13, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' }}>
              Cancelar
            </button>
            <button onClick={handleSave} disabled={saving} style={{
              display: 'flex', alignItems: 'center', gap: 7, padding: '9px 22px', borderRadius: 9, border: 'none',
              background: saving ? '#2a2a3a' : `linear-gradient(135deg, ${C.orange}, ${C.orangeHover})`,
              color: saving ? C.textMuted : '#fff', fontSize: 13, cursor: saving ? 'not-allowed' : 'pointer',
              fontFamily: 'DM Sans, sans-serif', fontWeight: 600,
              boxShadow: saving ? 'none' : '0 4px 14px rgba(249,115,22,0.35)',
            }}>
              {saving ? <div style={{ width: 14, height: 14, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} /> : <Save size={14} />}
              {saving ? 'Guardando...' : isNew ? 'Crear Plantilla' : 'Guardar'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

// ─── Template card ────────────────────────────────────────────────────────────

function TemplateCard({
  template,
  isAdmin,
  isGestor,
  onClick,
  onClone,
  onArchive,
  onViewVersions,
}: {
  template: ProjectTemplate;
  isAdmin: boolean;
  isGestor: boolean;
  onClick: () => void;
  onClone: () => void;
  onArchive: () => void;
  onViewVersions: () => void;
}) {
  const [hovering, setHovering] = useState(false);

  return (
    <div
      style={{
        background: template.isArchived ? 'rgba(18,18,26,0.6)' : C.card,
        border: `1px solid ${hovering ? C.borderHover : C.border}`,
        borderRadius: 14, padding: '20px', cursor: 'pointer', transition: 'all 0.15s',
        transform: hovering ? 'translateY(-2px)' : 'none',
        boxShadow: hovering ? '0 8px 24px rgba(0,0,0,0.3)' : 'none',
        opacity: template.isArchived ? 0.65 : 1,
      }}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
    >
      {/* Top row */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 10 }}>
        {/* Left - click to edit (only for gestor) */}
        <div onClick={isGestor ? onClick : undefined} style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0, cursor: isGestor ? 'pointer' : 'default' }}>
          <div style={{
            width: 36, height: 36, borderRadius: 9, flexShrink: 0,
            background: template.isArchived ? 'rgba(75,75,90,0.3)' : 'rgba(249,115,22,0.1)',
            border: `1px solid ${template.isArchived ? 'rgba(75,75,90,0.4)' : 'rgba(249,115,22,0.25)'}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <FileText size={15} color={template.isArchived ? C.textMuted : C.orange} />
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: template.isArchived ? C.textSecondary : C.textPrimary, fontFamily: 'DM Sans, sans-serif' }}>
                {template.name}
              </span>
              <span style={{
                padding: '1px 6px', borderRadius: 20, flexShrink: 0,
                background: 'rgba(249,115,22,0.12)', border: '1px solid rgba(249,115,22,0.3)',
                color: C.orange, fontSize: 10, fontWeight: 700, fontFamily: 'JetBrains Mono, monospace',
              }}>
                v{template.version ?? 1}
              </span>
              {template.isDefault && (
                <span style={{ padding: '1px 6px', borderRadius: 20, background: 'rgba(34,197,94,0.15)', border: '1px solid rgba(34,197,94,0.4)', color: C.success, fontSize: 10, fontWeight: 600, flexShrink: 0 }}>
                  DEFAULT
                </span>
              )}
              {template.isArchived && (
                <span style={{ padding: '1px 6px', borderRadius: 20, background: 'rgba(75,75,90,0.3)', border: '1px solid rgba(75,75,90,0.5)', color: C.textMuted, fontSize: 10, fontWeight: 600, flexShrink: 0 }}>
                  ARCHIVADA
                </span>
              )}
            </div>
            <div style={{ fontSize: 11, color: C.textMuted }}>{template.steps.length} pasos</div>
          </div>
        </div>

        {/* Action buttons */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0, marginLeft: 8 }}>
          {/* Versions */}
          <button
            onClick={e => { e.stopPropagation(); onViewVersions(); }}
            title="Ver historial de versiones"
            style={{ width: 28, height: 28, borderRadius: 7, border: `1px solid ${C.border}`, background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.textMuted }}
            onMouseEnter={e => { const b = e.currentTarget; b.style.borderColor = 'rgba(249,115,22,0.4)'; b.style.color = C.orange; }}
            onMouseLeave={e => { const b = e.currentTarget; b.style.borderColor = C.border; b.style.color = C.textMuted; }}
          >
            <Clock size={12} />
          </button>

          {/* Clone - gestor only */}
          {isGestor && (
            <button
              onClick={e => { e.stopPropagation(); onClone(); }}
              title="Duplicar plantilla"
              style={{ width: 28, height: 28, borderRadius: 7, border: `1px solid ${C.border}`, background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.textMuted }}
              onMouseEnter={e => { const b = e.currentTarget; b.style.borderColor = 'rgba(34,197,94,0.4)'; b.style.color = C.success; }}
              onMouseLeave={e => { const b = e.currentTarget; b.style.borderColor = C.border; b.style.color = C.textMuted; }}
            >
              <Copy size={12} />
            </button>
          )}

          {/* Archive / Unarchive - admin only */}
          {isAdmin && (
            <button
              onClick={e => { e.stopPropagation(); onArchive(); }}
              title={template.isArchived ? 'Desarchivar' : 'Archivar'}
              style={{ width: 28, height: 28, borderRadius: 7, border: `1px solid ${C.border}`, background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.textMuted }}
              onMouseEnter={e => { const b = e.currentTarget; b.style.borderColor = 'rgba(245,158,11,0.4)'; b.style.color = C.warning; }}
              onMouseLeave={e => { const b = e.currentTarget; b.style.borderColor = C.border; b.style.color = C.textMuted; }}
            >
              {template.isArchived ? <ArchiveRestore size={12} /> : <Archive size={12} />}
            </button>
          )}

          {/* Edit - gestor only */}
          {isGestor && (
            <button
              onClick={e => { e.stopPropagation(); onClick(); }}
              title="Editar plantilla"
              style={{ width: 28, height: 28, borderRadius: 7, border: `1px solid ${C.border}`, background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.textMuted }}
              onMouseEnter={e => { const b = e.currentTarget; b.style.borderColor = C.borderHover; b.style.color = C.textSecondary; }}
              onMouseLeave={e => { const b = e.currentTarget; b.style.borderColor = C.border; b.style.color = C.textMuted; }}
            >
              <Edit2 size={12} />
            </button>
          )}
        </div>
      </div>

      {template.description && (
        <p onClick={isGestor ? onClick : undefined} style={{ fontSize: 12, color: C.textSecondary, margin: '0 0 14px', lineHeight: 1.5, cursor: isGestor ? 'pointer' : 'default' }}>
          {template.description}
        </p>
      )}

      <StepFlowPreview steps={template.steps} />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 14, paddingTop: 12, borderTop: `1px solid ${C.border}` }}>
        {template.parentTemplate ? (
          <span style={{ fontSize: 10, color: C.textMuted, fontFamily: 'JetBrains Mono, monospace' }}>
            Copia de: {template.parentTemplate.name}
          </span>
        ) : <span />}
        <span style={{ fontSize: 10, color: C.textMuted, fontFamily: 'JetBrains Mono, monospace' }}>
          {new Date(template.createdAt).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' })}
        </span>
      </div>
    </div>
  );
}

// ─── Main TemplatesPage ───────────────────────────────────────────────────────

export default function TemplatesPage() {
  const { user } = useAuthStore();
  const isAdmin = user?.roles?.includes('ROLE_ADMIN') ?? false;
  const isGestor = (user?.roles?.includes('ROLE_GESTOR') || user?.roles?.includes('ROLE_ADMIN')) ?? false;

  const [modalTemplate, setModalTemplate] = useState<ProjectTemplate | null | 'new'>(null);
  const [versionsModal, setVersionsModal] = useState<{ id: number; name: string } | null>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showArchived, setShowArchived] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState<{
    template: ProjectTemplate;
    action: 'archive' | 'unarchive';
  } | null>(null);
  const [cloningId, setCloningId] = useState<number | null>(null);

  const qc = useQueryClient();

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const { data: apiData, isLoading } = useQuery({
    queryKey: ['templates', showArchived],
    queryFn: () => templatesApi.list(showArchived && isAdmin),
    retry: false,
  });

  const allTemplates: ProjectTemplate[] = apiData ?? [];

  const cloneMutation = useMutation({
    mutationFn: (id: number) => templatesApi.clone(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['templates'] });
      setCloningId(null);
    },
    onError: () => setCloningId(null),
  });

  const archiveMutation = useMutation({
    mutationFn: ({ id, archived }: { id: number; archived: boolean }) =>
      archived ? templatesApi.archive(id) : templatesApi.unarchive(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['templates'] });
      setConfirmArchive(null);
    },
  });

  const handleClone = (template: ProjectTemplate) => {
    setCloningId(template.id);
    cloneMutation.mutate(template.id);
  };

  const handleSaved = (_t: ProjectTemplate) => {
    qc.invalidateQueries({ queryKey: ['templates'] });
    setModalTemplate(null);
  };

  const handleDeleted = (_id: number) => {
    qc.invalidateQueries({ queryKey: ['templates'] });
    setModalTemplate(null);
  };

  return (
    <div style={{ padding: isMobile ? '20px 16px' : '32px', minHeight: '100vh', background: C.bg, fontFamily: 'DM Sans, sans-serif' }}>
      {/* Header */}
      <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'flex-start', gap: isMobile ? 16 : 0, marginBottom: 28 }}>
        <div>
          <h1 style={{ fontFamily: 'Syne, sans-serif', fontWeight: 900, fontSize: isMobile ? 24 : 28, color: '#fff', margin: '0 0 6px', letterSpacing: '-0.03em' }}>Plantillas</h1>
          <p style={{ fontSize: 13, color: C.textSecondary, margin: 0 }}>
            {allTemplates.length} plantilla{allTemplates.length !== 1 ? 's' : ''} de flujo
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', width: isMobile ? '100%' : 'auto' }}>
          {/* Show archived toggle (admin only) */}
          {isAdmin && (
            <button
              onClick={() => setShowArchived(v => !v)}
              style={{
                display: 'flex', alignItems: 'center', gap: 7, padding: '8px 14px', borderRadius: 9,
                border: `1px solid ${showArchived ? 'rgba(245,158,11,0.4)' : C.border}`,
                background: showArchived ? 'rgba(245,158,11,0.08)' : 'transparent',
                color: showArchived ? C.warning : C.textSecondary,
                fontSize: 12, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', fontWeight: 500,
                transition: 'all 0.15s',
              }}
            >
              <Archive size={13} />
              {showArchived ? 'Ocultar archivadas' : 'Ver archivadas'}
            </button>
          )}
          {isGestor && (
            <button
              onClick={() => setModalTemplate('new')}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                background: `linear-gradient(135deg, ${C.orange}, ${C.orangeHover})`,
                border: 'none', borderRadius: 10, padding: '10px 18px', color: '#fff',
                fontSize: 13, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', fontWeight: 600,
                boxShadow: '0 4px 14px rgba(249,115,22,0.35)',
                width: isMobile ? '100%' : 'auto', justifyContent: isMobile ? 'center' : undefined,
              }}
            >
              <Plus size={16} /> Nueva Plantilla
            </button>
          )}
        </div>
      </div>

      {/* Info banner */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', background: 'rgba(249,115,22,0.06)', border: '1px solid rgba(249,115,22,0.2)', borderRadius: 10, marginBottom: 24 }}>
        <Info size={14} color={C.orange} />
        <span style={{ fontSize: 12, color: C.textSecondary }}>
          Las plantillas definen el flujo de departamentos. Usa <strong style={{ color: C.textPrimary }}>Clonar</strong> para crear variantes sin modificar el original, y <strong style={{ color: C.textPrimary }}>Ver versiones</strong> para consultar el historial de cambios.
        </span>
      </div>

      {/* Content */}
      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
          <div style={{ width: 32, height: 32, border: `3px solid ${C.border}`, borderTopColor: C.orange, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
          {allTemplates.map(t => (
            <div key={t.id} style={{ position: 'relative' }}>
              {cloningId === t.id && (
                <div style={{
                  position: 'absolute', inset: 0, background: 'rgba(10,10,15,0.75)',
                  borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10,
                }}>
                  <div style={{ width: 24, height: 24, border: `3px solid ${C.border}`, borderTopColor: C.orange, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                </div>
              )}
              <TemplateCard
                template={t}
                isAdmin={isAdmin}
                isGestor={isGestor}
                onClick={() => setModalTemplate(t)}
                onClone={() => handleClone(t)}
                onArchive={() => setConfirmArchive({ template: t, action: t.isArchived ? 'unarchive' : 'archive' })}
                onViewVersions={() => setVersionsModal({ id: t.id, name: t.name })}
              />
            </div>
          ))}
          {allTemplates.length === 0 && (
            <div style={{ gridColumn: '1/-1', padding: '48px 16px', textAlign: 'center', color: C.textMuted, fontSize: 14 }}>
              No hay plantillas. Crea una nueva.
            </div>
          )}
        </div>
      )}

      {/* Confirm archive modal */}
      {confirmArchive && (
        <>
          <div onClick={() => setConfirmArchive(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 400, backdropFilter: 'blur(4px)' }} />
          <div style={{
            position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
            zIndex: 401, width: '90%', maxWidth: 380,
            background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: '24px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {confirmArchive.action === 'archive'
                  ? <Archive size={15} color={C.warning} />
                  : <ArchiveRestore size={15} color={C.warning} />}
              </div>
              <div>
                <h3 style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 15, color: '#fff', margin: 0 }}>
                  {confirmArchive.action === 'archive' ? 'Archivar plantilla' : 'Desarchivar plantilla'}
                </h3>
                <p style={{ fontSize: 11, color: C.textMuted, margin: 0 }}>{confirmArchive.template.name}</p>
              </div>
            </div>
            <p style={{ fontSize: 13, color: C.textSecondary, marginBottom: 20, lineHeight: 1.5 }}>
              {confirmArchive.action === 'archive'
                ? 'La plantilla se ocultará del listado principal. Los proyectos existentes no se verán afectados.'
                : 'La plantilla volverá a aparecer en el listado principal.'}
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setConfirmArchive(null)}
                style={{ padding: '8px 16px', borderRadius: 8, border: `1px solid ${C.border}`, background: 'transparent', color: C.textSecondary, fontSize: 13, cursor: 'pointer' }}
              >
                Cancelar
              </button>
              <button
                onClick={() => archiveMutation.mutate({ id: confirmArchive.template.id, archived: confirmArchive.action === 'archive' })}
                disabled={archiveMutation.isPending}
                style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: C.warning, color: '#fff', fontSize: 13, cursor: 'pointer', fontWeight: 600 }}
              >
                {archiveMutation.isPending ? '...' : confirmArchive.action === 'archive' ? 'Archivar' : 'Desarchivar'}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Template edit/create modal */}
      {modalTemplate !== null && (
        <TemplateModal
          template={modalTemplate === 'new' ? undefined : modalTemplate}
          onClose={() => setModalTemplate(null)}
          onSaved={handleSaved}
          onDeleted={handleDeleted}
          isMobile={isMobile}
        />
      )}

      {/* Versions modal */}
      {versionsModal && (
        <VersionsModal
          templateId={versionsModal.id}
          templateName={versionsModal.name}
          onClose={() => setVersionsModal(null)}
        />
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
